import { useMemo } from 'react';
import { ENDPOINTS } from './../../endpoints';
import { Service } from './../base';
import dynamicAxios from './../../utils/axios';
import useServices from '../../hooks/useServices';
import {
  CheckInvoicesStatusResponse,
  CompleteCustomerDiscoveryResponse,
  CustomerDiscoveryBookedResponse,
  CreateOrganization,
  GetMissedOrgApplicationActivities,
  GrantCreditsPayload,
  ICreateOrganizationPayload,
  IndexCompanies,
  IndexOrganizations,
  IndexReferralCredits,
  IndexStripeSubscriptions,
  OrganizationDiscrepancy,
  PerformanceMetricsOutput,
  ReferralRequest,
  SlotDiscrepanciesAlertParams,
} from './types';
import {
  Company,
  Organization,
  OrgnaizationStructuredNote,
  ReferralCredit,
  SnoozedOrganization,
} from '@restworld/utility-types';
import {
  EmployerFilterType,
  FollowupsParams,
  FollowupsParamsGET,
  IndexFollowups,
  IndexStripePaymentMethods,
  SlotPricing,
} from '../useEmployers';
import { Id } from '../../utils/common_types';

/**
 * Upper bound on the referral onboarding prefill request.
 *
 * The venue lookup runs while `/getting-started` is still showing its loading state, so an
 * unbounded request would hold the sign-up page blank. Generous for an indexed lookup, short
 * enough that a dead endpoint costs a couple of seconds and nothing else.
 */
const REFERRAL_PREFILL_TIMEOUT_MS = 2500;

class ServiceOrganizations extends Service {
  indexOrganizations({
    limit,
    offset,
    order_by,
    filters,
  }: {
    limit: number;
    offset: number;
    order_by?: string[];
    filters?: EmployerFilterType;
  }) {
    return this.axiosInstance.get<IndexOrganizations>(ENDPOINTS.organizations.root, {
      params: { limit, offset, order_by, ...filters },
    });
  }

  createOrganization(params: ICreateOrganizationPayload) {
    return this.axiosInstance.post<CreateOrganization>(ENDPOINTS.organizations.root, params);
  }

  /**
   * Public endpoint to look up a referral code and return the referrer info.
   * Used by /getting-started?ref=XXXX to display referrer restaurant name.
   */
  lookupReferralCode(referralCode: string) {
    return this.axiosInstance.get<{
      referral_code: string;
      referrer_restaurant_name: string;
      referrer_organization_id: string;
      // Free days the invited organization receives; absent on an older backend.
      credits_invitee?: number;
    }>(`${ENDPOINTS.organizations.root}/referral_lookup`, {
      params: { referral_code: referralCode },
    });
  }

  /**
   * Public endpoint to look up a partner code and return the info needed by the
   * /getting-started?partner=XXXX intro screen. Resolves B2B ambassador
   * ("procacciatore") codes, worker "porta un locale" codes and static
   * acquisition campaign codes. `source_type` distinguishes them and
   * `free_days` is the backend-driven credit amount. For workers,
   * `partner_name` is the first name only.
   */
  lookupPartnerCode(partnerCode: string) {
    return this.axiosInstance.get<{
      partner_code: string;
      source_type: 'ambassador' | 'campaign' | 'worker';
      free_days: number;
      partner_name?: string;
      campaign_name?: string | null;
    }>(`${ENDPOINTS.organizations.root}/partner_lookup`, {
      params: { partner_code: partnerCode },
    });
  }

  /**
   * Public endpoint backing the onboarding prefill for a prospect arriving from an accepted
   * referral invite at /getting-started?ref=XXXX.
   *
   * With the referral code alone it answers with the venue that asked for the invite, and
   * only when the referrer has exactly one outstanding request. That is a hint for the
   * search box, never a selection: the user still has to pick a Google suggestion, because
   * text typed without choosing one leaves gmaps_place_id null and the Google enrichment
   * never runs for that venue.
   *
   * Passing `prospect_gmaps_place_id` — the venue the user actually picked — is the only way
   * to get the email they gave. A referral code travels inside an email that can be
   * forwarded, so a match on the chosen venue is the only evidence we have that the person
   * filling in the form is the person who asked.
   *
   * An empty object is the normal answer when there is nothing to offer.
   *
   * Explicitly timed out because the shared Axios instance sets no default and the venue
   * lookup gates the onboarding render: without a bound, a hanging request would leave the
   * sign-up page blank indefinitely. Onboarding must never depend on this endpoint — losing
   * the prefill costs the user a search box they fill in themselves.
   */
  getReferralOnboardingPrefill(params: { referral_code: string; prospect_gmaps_place_id?: string }) {
    return this.axiosInstance.get<{
      venue?: { name: string; address: string | null };
      email?: string;
    }>(`${ENDPOINTS.organizations.root}/referral_requests/prefill`, {
      params,
      timeout: REFERRAL_PREFILL_TIMEOUT_MS,
    });
  }

  /**
   * Authenticated endpoint to get referral bonus info for the logged-in user's organization.
   * Returns { referrer_restaurant_name, credited_units } if the org was referred, or null.
   * `credited_units` (free days granted) is absent on an older backend.
   */
  getReferralBonusInfo() {
    return this.axiosInstance.get<{ referrer_restaurant_name: string; credited_units?: number } | null>(
      ENDPOINTS.organizations.referralBonusInfo
    );
  }

  updateOrganization(data: Partial<Organization>) {
    return this.axiosInstance.patch<Id>(`${ENDPOINTS.organizations.root}/${data.id}`, data);
  }

  showOrganization(organizationId: string, context: string) {
    return this.axiosInstance.get<Organization>(`${ENDPOINTS.organizations.root}/${organizationId}`, {
      params: { context },
    });
  }

  fetchStripeSubscriptions({ companyId, organizationId }: { companyId?: string; organizationId?: string }) {
    return this.axiosInstance.get<IndexStripeSubscriptions>(ENDPOINTS.companies.subscriptions, {
      params: { company_id: companyId, organization_id: organizationId },
    });
  }

  // COMPANIES //
  listCompanies({
    organizationId,
    limit,
    offset,
    context,
    filterDefault,
  }: {
    context: string;
    organizationId?: string;
    limit?: number;
    offset?: number;
    filterDefault?: boolean;
  }) {
    return this.axiosInstance.get<IndexCompanies>(ENDPOINTS.companies.root, {
      params: { organization_id: organizationId, limit, offset, context, default: filterDefault },
    });
  }

  createCompany(data: Partial<Company>) {
    return this.axiosInstance.post<Id>(ENDPOINTS.companies.root, data);
  }

  updateCompany(data: Partial<Company>) {
    return this.axiosInstance.patch<Id>(`${ENDPOINTS.companies.root}/${data.id}`, data);
  }

  deleteCompany(companyId?: string) {
    return this.axiosInstance.delete<string>(`${ENDPOINTS.companies.root}/${companyId}`);
  }

  // STRIPE //
  fetchStripeCustomerPortal({ companyId, organizationId }: { companyId?: string; organizationId?: string }) {
    return this.axiosInstance.get<string>(ENDPOINTS.companies.stripeCustomerPortal, {
      params: { company_id: companyId, organization_id: organizationId },
    });
  }

  fetchStripePaymentMethods({ organizationId, companyId }: { organizationId?: string; companyId?: string }) {
    return this.axiosInstance.get<IndexStripePaymentMethods>(ENDPOINTS.organizations.paymentMethods, {
      params: { organization_id: organizationId, company_id: companyId },
    });
  }

  detachPaymentMethod({ paymentMethodId }: { paymentMethodId: string }) {
    return this.axiosInstance.delete<string>(`${ENDPOINTS.organizations.paymentMethods}/${paymentMethodId}`);
  }

  updateDefaultPaymentMethod({
    stripeCustomerId,
    paymentMethodId,
  }: {
    stripeCustomerId: string;
    paymentMethodId: string;
  }) {
    return this.axiosInstance.patch<string>(`${ENDPOINTS.organizations.updateStripeCustomer}/${stripeCustomerId}`, {
      payment_method_id: paymentMethodId,
    });
  }

  attachPaymentMethod({
    stripeCustomerId,
    paymentMethodId,
    isDefault,
  }: {
    stripeCustomerId: string;
    paymentMethodId: string;
    isDefault?: boolean;
  }) {
    return this.axiosInstance.post<string>(ENDPOINTS.organizations.paymentMethods, {
      stripe_customer_id: stripeCustomerId,
      payment_method_id: paymentMethodId,
      is_default: isDefault,
    });
  }

  createPaymentMethodSetupIntent({ companyId }: { companyId: string }) {
    return this.axiosInstance.post<{ client_secret: string; setup_intent_id: string }>(
      ENDPOINTS.organizations.paymentMethodSetupIntent,
      { company_id: companyId }
    );
  }

  sendSubscriptionEmail({ companyId, paymentLink }: { companyId?: string; paymentLink?: string }) {
    return this.axiosInstance.post<string>(ENDPOINTS.companies.subscriptionEmail, {
      company_id: companyId,
      payment_link: paymentLink,
    });
  }

  // FOLLOWUPS //
  fetchFollowups = (params?: FollowupsParamsGET) => {
    return this.axiosInstance.get<{ data: IndexFollowups[] }>(ENDPOINTS.organizations.followups, {
      params: {
        organization_id: params?.organization_id,
        limit: params?.limit,
        offset: params?.offset,
      },
    });
  };

  createEmployerFollowup = (params: Partial<FollowupsParams>) => {
    const { csmResponsibleId, followupContactMethod, followupStatus, notes, referentContactId, organizationId } =
      params;
    return this.axiosInstance.post<{
      data: IndexFollowups;
    }>(ENDPOINTS.organizations.followups, {
      csm_responsible_id: csmResponsibleId,
      organization_id: organizationId,
      referent_contact_id: referentContactId,
      followup_contact_method: followupContactMethod,
      followup_status: followupStatus,
      notes,
    });
  };

  updateEmployerFollowup = (id: number, params: Partial<FollowupsParams>) => {
    const { csmResponsibleId, organizationId, followupContactMethod, followupStatus, notes, referentContactId } =
      params;

    return this.axiosInstance.put<{
      data: IndexFollowups;
    }>(`${ENDPOINTS.organizations.followups}/${id}`, {
      csm_responsible_id: csmResponsibleId,
      organization_id: organizationId,
      referent_contact_id: referentContactId,
      followup_contact_method: followupContactMethod,
      followup_status: followupStatus,
      notes,
    });
  };

  fetchFollowup = (id: number) => {
    return this.axiosInstance.get<{
      data: IndexFollowups;
    }>(`${ENDPOINTS.organizations.followups}/${id}`);
  };

  deleteEmployerFollowup = (id: number) => {
    return this.axiosInstance.delete<string>(`${ENDPOINTS.organizations.followups}/${id}`);
  };

  createOrganizationStructuredNote(note: Partial<OrgnaizationStructuredNote>) {
    return this.axiosInstance.post<string>(ENDPOINTS.organizations.structuredNotes, note);
  }

  updateOrganizationStructuredNote(note: Partial<OrgnaizationStructuredNote>) {
    return this.axiosInstance.patch<string>(`${ENDPOINTS.organizations.structuredNotes}/${note.organization_id}`, note);
  }

  getMissedOrgApplicationActivities = (params: { limit?: number; offset?: number }) => {
    return this.axiosInstance.get<{
      data: GetMissedOrgApplicationActivities[];
      hits: number;
    }>(ENDPOINTS.organizations.missedOrgApplicationActivities, {
      params,
    });
  };
  getFlexibleSlotPrices = () => {
    return this.axiosInstance.get<SlotPricing[]>(ENDPOINTS.organizations.flexibleSlotPrices);
  };

  getDiscrepanciesBetweenSlotsAndActiveJobPositions({ organizationId }: SlotDiscrepanciesAlertParams) {
    return this.axiosInstance.get<OrganizationDiscrepancy>(`${ENDPOINTS.organizations.discrepancies}`, {
      params: { organization_id: organizationId },
    });
  }
  getOrganizationsWithSlotAndJpDiscrepancies({ csmId, isAdmin }: { csmId?: string; isAdmin?: boolean }) {
    return this.axiosInstance.get<OrganizationDiscrepancy[]>(ENDPOINTS.organizations.discrepancies, {
      params: {
        csm_id: csmId,
        is_admin: isAdmin,
      },
    });
  }

  getOrganizationPerformanceMetrics({ organizationId }: { organizationId?: string } = {}) {
    return this.axiosInstance.get<PerformanceMetricsOutput>(ENDPOINTS.organizations.performanceMetrics, {
      params: {
        organization_id: organizationId,
      },
    });
  }

  insertOrUpdateSnoozedOrganization(params: Partial<SnoozedOrganization>) {
    return this.axiosInstance.post<Id>(ENDPOINTS.organizations.snooze, params);
  }

  reactivateSnoozedOrganization(organizationId: string) {
    return this.axiosInstance.delete<string>(`${ENDPOINTS.organizations.snooze}/${organizationId}`);
  }

  checkInvoicesStatus({ organizationId }: { organizationId: string }) {
    return this.axiosInstance.get<CheckInvoicesStatusResponse>(ENDPOINTS.organizations.checkInvoicesStatus, {
      params: { organization_id: organizationId },
    });
  }

  /**
   * Operator confirms a completed customer discovery call: grants the reward
   * credits and stamps the org's cooldown.
   */
  completeCustomerDiscovery({ organizationId }: { organizationId: string }) {
    return this.axiosInstance.post<CompleteCustomerDiscoveryResponse>(
      ENDPOINTS.organizations.customerDiscoveryComplete,
      { organization_id: organizationId }
    );
  }

  /**
   * Operator marks a customer discovery call as booked (scheduled): pauses
   * Intercom reminders for 30 days. Grants no credits and starts no cooldown.
   */
  markCustomerDiscoveryBooked({ organizationId }: { organizationId: string }) {
    return this.axiosInstance.post<CustomerDiscoveryBookedResponse>(ENDPOINTS.organizations.customerDiscoveryBooked, {
      organization_id: organizationId,
    });
  }

  // ── Referral Requests ──────────────────────────────────────────────

  /**
   * Public endpoint: prospect submits a referral request to an employer.
   */
  createReferralRequest(data: {
    restaurant_id: string;
    prospect_restaurant_name: string;
    prospect_restaurant_address?: string;
    prospect_gmaps_place_id: string;
    prospect_email: string;
  }) {
    return this.axiosInstance.post<{ id: string; status: string }>(ENDPOINTS.organizations.referralRequests, data);
  }

  /**
   * Public endpoint: check if a referral request already exists for this prospect + restaurant.
   */
  checkReferralRequestExistence(data: { prospect_gmaps_place_id: string; restaurant_id: string }) {
    return this.axiosInstance.post<{
      exists: boolean;
      status?: string;
      referral_code?: string;
    }>(ENDPOINTS.organizations.checkReferralRequestExistence, data);
  }

  /**
   * Authenticated endpoint: list referral requests for the logged-in employer's organization.
   */
  listReferralRequests(params?: { limit?: number; status?: string[] }) {
    return this.axiosInstance.get<ReferralRequest[]>(ENDPOINTS.organizations.referralRequests, {
      params: { limit: params?.limit, status: params?.status },
    });
  }

  /**
   * Authenticated endpoint: update a pending referral request (accept or reject).
   */
  updateReferralRequest(id: string, data: { status: 'accepted' | 'rejected' }) {
    return this.axiosInstance.patch<{ status: string }>(`${ENDPOINTS.organizations.referralRequests}/${id}`, data);
  }

  /**
   * Internal (rw_member-only): list every credit grant on an organization,
   * newest first, plus the balance available across all sources.
   *
   * `grant_reason` is only included for an authenticated rw_member — the
   * backend omits the key entirely otherwise.
   */
  listCredits(organizationId: string) {
    return this.axiosInstance.get<IndexReferralCredits>(`${ENDPOINTS.organizations.root}/${organizationId}/credits`);
  }

  /**
   * Internal (rw_member-only): issue free credits to an organization.
   *
   * The source is hard-coded to `manual_allowance` because it is the only one
   * the backend accepts here — `bulk_sales` requires a matching invoice and
   * grants a payment-method bypass, and the referral sources are
   * system-generated. The granting team member is resolved server-side from the
   * JWT, so it is deliberately not part of the payload.
   */
  grantCredits({ organizationId, units, grantReason }: GrantCreditsPayload) {
    return this.axiosInstance.post<{ data: ReferralCredit }>(
      `${ENDPOINTS.organizations.root}/${organizationId}/credits`,
      { credit_source: 'manual_allowance', units, grant_reason: grantReason }
    );
  }

  upsertProspect(params: {
    id: string;
    last_completed_step: string;
    restaurant_name?: string;
    address?: string;
    gmaps_place_id?: string;
    country?: string;
    contact_name?: string;
    contact_surname?: string;
    contact_email?: string;
    contact_phone?: string;
    job_position_data?: Record<string, unknown>;
    utm_source?: string;
    partner_code?: string;
    flow_variant?: string;
    // Meta match signals forwarded to the server-side RestaurantSelected CAPI
    // conversion (backend rebuilds `fbc` from `fbclid`; `fbp` sent raw).
    fbclid?: string;
    fbp?: string;
  }) {
    return this.axiosInstance.put<{ id: string }>(ENDPOINTS.organizations.prospects, params);
  }
}

export function useOrganizationsService() {
  const { baseUrl, jwt } = useServices();
  return useMemo(() => new ServiceOrganizations(dynamicAxios(baseUrl, jwt)), [baseUrl, jwt]);
}

export const createOrganizationService = ({ baseUrl, jwt }: { baseUrl: string; jwt?: string }) =>
  new ServiceOrganizations(dynamicAxios(baseUrl, jwt));

export default useOrganizationsService;

export type {
  IndexCompanies,
  GetMissedOrgApplicationActivities,
  GetMissedOrgApplicationActivitiesCategory,
  GrantCreditsPayload,
  ICreateOrganizationPayload,
  IndexReferralCredits,
  CheckInvoicesStatusResponse,
  CompleteCustomerDiscoveryResponse,
  CustomerDiscoveryBookedResponse,
  ReferralRequest,
} from './types';
